import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";
import { useLanguageAwareSEO } from "@/hooks/useLanguageAwareSEO";
import { breadcrumbSchema, calculatorToolSchema } from "@/utils/structuredData";

export default function MeanMedianModeCalculator() {
  useLanguageAwareSEO([
    (lang) => breadcrumbSchema([
      { name: "Home", url: "https://calclab.app" },
      { name: "Math", url: "https://calclab.app/math" },
      { name: "Mean, Median, Mode Calculator", url: "https://calclab.app/calculator/mean-median-mode" }
    ], lang),
    (lang) => calculatorToolSchema(
      "Mean, Median, Mode Calculator",
      "Calculate measures of central tendency for any data set. Free online statistics calculator.",
      "https://calclab.app/calculator/mean-median-mode",
      lang
    )
  ]);
  const [data, setData] = useState("");
  const [result, setResult] = useState<{
    mean: number;
    median: number;
    mode: number[] | string;
    count: number;
    sum: number;
  } | null>(null);

  const calculate = () => {
    const numbers = data
      .split(/[\s,]+/)
      .map(n => parseFloat(n.trim()))
      .filter(n => !isNaN(n));

    if (numbers.length === 0) {
      toast({
        title: "Invalid Input",
        description: "Please enter valid numbers separated by spaces or commas.",
        variant: "destructive",
      });
      return;
    }

    // Mean
    const sum = numbers.reduce((acc, n) => acc + n, 0);
    const mean = sum / numbers.length;

    // Median
    const sorted = [...numbers].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    const median = sorted.length % 2 === 0
      ? (sorted[mid - 1] + sorted[mid]) / 2
      : sorted[mid];

    // Mode
    const frequency: { [key: number]: number } = {};
    numbers.forEach(n => {
      frequency[n] = (frequency[n] || 0) + 1;
    });
    const maxFreq = Math.max(...Object.values(frequency));
    const modes = Object.keys(frequency)
      .filter(key => frequency[parseFloat(key)] === maxFreq)
      .map(key => parseFloat(key));
    
    const mode = maxFreq === 1 ? "No mode" : modes;

    setResult({
      mean,
      median,
      mode,
      count: numbers.length,
      sum,
    });

    toast({
      title: "Calculation Complete",
      description: `Analyzed ${numbers.length} numbers`,
    });
  };

  const reset = () => {
    setData("");
    setResult(null);
  };

  return (
    <PageLayout 
      calculatorName="Mean, Median & Mode Calculator" 
      calculatorPath="/calculator/mean-median-mode"
      category="math"
      subcategory="Statistics"
    >
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle>Mean, Median, Mode Calculator</CardTitle>
            <CardDescription>
              Calculate measures of central tendency
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="data">Data Set (separate with spaces or commas)</Label>
              <Textarea
                id="data"
                value={data}
                onChange={(e) => setData(e.target.value)}
                placeholder="e.g., 5, 10, 15, 20, 25 or 5 10 15 20 25"
                rows={4}
              />
            </div>

            <div className="flex gap-4">
              <Button onClick={calculate} className="flex-1">Calculate</Button>
              <Button onClick={reset} variant="outline" className="flex-1">Reset</Button>
            </div>

            {result && (
              <>
                <div className="space-y-4">
                <Card className="bg-muted/50">
                  <CardHeader>
                    <CardTitle className="text-lg">Results</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-muted-foreground">Count</p>
                        <p className="text-2xl font-bold">{result.count}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Sum</p>
                        <p className="text-2xl font-bold">{result.sum.toFixed(2)}</p>
                      </div>
                    </div>
                    <hr className="border-muted" />
                    <div>
                      <p className="text-sm text-muted-foreground">Mean (Average)</p>
                      <p className="text-2xl font-bold">{result.mean.toFixed(4)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Median (Middle Value)</p>
                      <p className="text-2xl font-bold">{result.median.toFixed(4)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Mode (Most Frequent)</p>
                      <p className="text-2xl font-bold">
                        {typeof result.mode === "string"
                          ? result.mode
                          : result.mode.join(", ")}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <CalculatorSuperCalcCTA config={calculatorCTAs["mean-median-mode"]} trigger={result?.mean} />
              </>
            )}

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-sm">Definitions</CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground space-y-2">
                <p><strong>Mean:</strong> Sum of all values divided by count</p>
                <p><strong>Median:</strong> Middle value when sorted</p>
                <p><strong>Mode:</strong> Most frequently occurring value(s)</p>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
